import {
  ActionConfirmationModal,
  AppModal,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  StepProgress,
} from "@/components/ui";
import { FormCancelButton } from "@/components/ui/FormFields/FormCancelButton/FormCancelButton";
import { useUser } from "@/components/UserProvider";
import { retrieveMatterSettingsQueryOptions } from "@/lib/data/react-query";
import {
  retrieveFirmFilesOptions,
  useGenerateFirmFileDocument,
  useGenerateFirmFilePdf,
} from "@/lib/data/react-query/firm/firm-file";
import { getFirmPhonesQueryOptions } from "@/lib/data/react-query/firm/firm-phones";
import { retrieveMatterQueryOptions } from "@/lib/data/react-query/matters/matter";
import { useGenerateMatterFilePdf } from "@/lib/data/react-query/matters/matter-file";
import { useGenerateMatterTemplateDocument } from "@/lib/data/react-query/matters/matter-template";
import { createFirmSignatureRequestV3 } from "@/lib/data/requests/firm/firm-signature-request";
import { createMatterSignatureRequestV3 } from "@/lib/data/requests/matters/matter-signature-request";
import type {
  APIError,
  FirmFile,
  MatterFile,
  MatterDocumentSubItem,
  MatterTemplate,
} from "@/lib/types";
import { toastAPIErrorMessage } from "@/lib/utils/axiosUtils";
import { getFileNameParts } from "@/lib/utils/matterFile";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import {
  dispatchSignatureRequestCreated,
  invalidateSignatureRequestCreateRelatedQueries,
} from "@/lib/utils/signature-request-created";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { AxiosError } from "axios";
import dayjs from "dayjs";
import { ChevronDown } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import type { LayoutField } from "./SignatureRequestPdfLayoutEditor.constants";
import {
  buildFirmSignatureRequestFieldsPayload,
  buildMatterSignatureRequestV2FieldsPayload,
  buildSignatureRequestNotificationWarningDescription,
  type FirmSignatureRequestFieldPayload,
  type MatterSignatureRequestV2FieldPayload,
} from "./signatureRequestCreateHelpers";
import {
  buildSignatureRequestMergeInputs,
  getBaseKeyForSignatureRequestVariable,
} from "./signatureRequestMergeInputs";
import {
  getSignatureRequestSignerId,
  getSignatureRequestSignerSlotIndex,
  isContactSigner,
  isUserSigner,
  type SignatureRequestSigner,
} from "./signatureRequestSigners";
import {
  buildUniqueGeneratedFileName,
  buildUniqueGeneratedFirmFileName,
  getEffectiveSignatureRequestSteps,
  isSignatureRequestDocumentPdf,
  mergeFirmFileVariableLists,
} from "./signatureRequestWizardUtils";
import {
  DestinationStep,
  MapVariablesStep,
  ReviewAndSendStep,
  SelectDocumentStep,
} from "./steps";
import { SendRequestStep } from "./steps/SendRequestStep";
import type { SignatureRequestPreviewDocument } from "./steps/SignatureRequestDocumentPreview";
import { maskGeneratedSignatureRequestPdf } from "./generatedSignatureRequestPdf";
import { useEditedSignatureRequestDocument } from "./useEditedSignatureRequestDocument";

interface CreateSignatureRequestSlideoverProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  matterId?: string;
  subItem?: MatterDocumentSubItem;
  title: string;
  description: string;
}

export const CreateSignatureRequestSlideover = ({
  open,
  setOpen,
  matterId,
  subItem,
  title,
}: CreateSignatureRequestSlideoverProps) => {
  const { hasActionPermission } = useUser();
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedMatterId, setSelectedMatterId] = useState(matterId ?? "");
  const [selectedMatterFileId, setSelectedMatterFileId] =
    useState<MatterFile["id"]>("");
  const [selectedMatterTemplateId, setSelectedMatterTemplateId] =
    useState<MatterTemplate["id"]>("");
  const [selectedFirmFile, setSelectedFirmFile] = useState<FirmFile | null>(
    null,
  );
  const [selectedDestinationFolderId, setSelectedDestinationFolderId] =
    useState<MatterFile["id"]>("");
  const [mappedVariableValues, setMappedVariableValues] = useState<
    Record<string, string>
  >({});
  const [deliveryMethod, setDeliveryMethod] = useState<
    "email" | "text" | "both"
  >("email");
  const [isOrdered, setIsOrdered] = useState(false);
  const [recipientSlotOrder, setRecipientSlotOrder] = useState<string[]>([]);
  const [signers, setSigners] = useState<SignatureRequestSigner[]>([]);
  const [expirationDate, setExpirationDate] = useState("");
  const [expirationDateError, setExpirationDateError] = useState<string | null>(
    null,
  );
  const [generatedPreviewMatterFile, setGeneratedPreviewMatterFile] =
    useState<MatterFile | null>(null);
  const [generatedPreviewFirmFile, setGeneratedPreviewFirmFile] =
    useState<FirmFile | null>(null);
  const [generatedPreviewKey, setGeneratedPreviewKey] = useState("");
  const [layoutFields, setLayoutFields] = useState<LayoutField[]>([]);
  const [isSubmittingSignatureRequest, setIsSubmittingSignatureRequest] =
    useState(false);
  const [isMaskingGeneratedPdf, setIsMaskingGeneratedPdf] = useState(false);
  const [submissionAction, setSubmissionAction] = useState<
    "send" | "create-only" | null
  >(null);
  const [isMissingVariablesModalOpen, setIsMissingVariablesModalOpen] =
    useState(false);
  const [missingVariableCount, setMissingVariableCount] = useState(0);
  const [
    notificationConfirmationDescription,
    setNotificationConfirmationDescription,
  ] = useState<string | null>(null);
  /**
   * Global e-sign app only: which flow the user chose on step 1 (Matter tab vs Firm tab).
   * Firm flow must not use matter APIs.
   */
  const [globalSignatureFlowKind, setGlobalSignatureFlowKind] = useState<
    "matter" | "firm"
  >("matter");
  const queryClient = useQueryClient();
  const {
    isRefreshing: isRefreshingEditedDocument,
    refreshError: documentRefreshError,
    refreshMatterFile: handleMatterFileSaved,
    refreshFirmFile: handleFirmFileSaved,
  } = useEditedSignatureRequestDocument({
    matterId: selectedMatterId,
    open,
    setMatterFile: setGeneratedPreviewMatterFile,
    setFirmFile: setGeneratedPreviewFirmFile,
  });
  const isGlobalSignatureWizard = !matterId;
  const matterApisEnabledInWizard =
    !isGlobalSignatureWizard || globalSignatureFlowKind === "matter";
  const { data: matterData } = useQuery({
    ...retrieveMatterQueryOptions(selectedMatterId),
    enabled: open && matterApisEnabledInWizard && Boolean(selectedMatterId),
  });
  const { data: matterSettingsData } = useQuery({
    ...retrieveMatterSettingsQueryOptions(),
    /** Template list is firm-wide; needed for Matter and Firm tabs (global) and matter-scoped flows. */
    enabled: open,
  });
  const { data: firmFilesData } = useQuery(retrieveFirmFilesOptions(open));
  const generateMatterTemplateDocument = useGenerateMatterTemplateDocument();
  const generateFirmFileDocument = useGenerateFirmFileDocument();
  const generateMatterFilePdf = useGenerateMatterFilePdf();
  const generateFirmFilePdf = useGenerateFirmFilePdf();
  const matter = matterData?.data;
  const selectedMatterFile = useMemo(() => {
    if (!matter || !selectedMatterFileId) {
      return null;
    }

    return (
      matter.files.find((file) => file.id === selectedMatterFileId) ?? null
    );
  }, [matter, selectedMatterFileId]);
  const selectedMatterTemplate = useMemo(
    () =>
      matterSettingsData?.data.templates.find(
        (template) => template.id === selectedMatterTemplateId,
      ) ?? null,
    [matterSettingsData?.data.templates, selectedMatterTemplateId],
  );
  const selectedDocument =
    useMemo<SignatureRequestPreviewDocument | null>(() => {
      if (selectedFirmFile) {
        return {
          type: "firm-file",
          document: selectedFirmFile,
        };
      }

      if (selectedMatterFile) {
        return {
          type: "matter-file",
          document: selectedMatterFile,
        };
      }

      if (selectedMatterTemplate) {
        return {
          type: "matter-template",
          document: selectedMatterTemplate,
        };
      }

      return null;
    }, [selectedFirmFile, selectedMatterFile, selectedMatterTemplate]);
  const canCreateFirmSignatureRequest =
    hasActionPermission(
      ACTION_PERMISSION_KEYS.firmSignatureRequest,
      ACTION_PERMISSION_VALUES.create,
    ) &&
    hasActionPermission(
      ACTION_PERMISSION_KEYS.firmSignatureRequestField,
      ACTION_PERMISSION_VALUES.create,
    );
  const canCreateMatterSignatureRequest = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterSignatureRequest,
    ACTION_PERMISSION_VALUES.create,
  );
  const canCreateSelectedSignatureRequest =
    selectedDocument?.type === "firm-file"
      ? canCreateFirmSignatureRequest
      : canCreateMatterSignatureRequest;
  const selectedDocumentIsPdf = isSignatureRequestDocumentPdf(selectedDocument);

  const useFirmLibraryDestination =
    selectedDocument?.type === "firm-file" ||
    (isGlobalSignatureWizard && globalSignatureFlowKind === "firm");
  const currentGenerationKey = useMemo(
    () =>
      JSON.stringify({
        selectedMatterFileId,
        selectedMatterTemplateId,
        selectedFirmFileId: selectedFirmFile?.id ?? "",
        mappedVariableValues: Object.entries(mappedVariableValues).sort(
          ([keyA], [keyB]) => keyA.localeCompare(keyB),
        ),
      }),
    [
      mappedVariableValues,
      selectedFirmFile?.id,
      selectedMatterFileId,
      selectedMatterTemplateId,
    ],
  );

  const selectedDocumentVariables = useMemo(() => {
    if (selectedDocument?.type === "matter-file") {
      if (
        generatedPreviewKey === currentGenerationKey &&
        generatedPreviewMatterFile
      ) {
        return generatedPreviewMatterFile.variables ?? [];
      }
      return selectedDocument.document.variables ?? [];
    }
    if (selectedDocument?.type === "matter-template") {
      if (
        generatedPreviewKey === currentGenerationKey &&
        generatedPreviewMatterFile
      ) {
        return generatedPreviewMatterFile.variables ?? [];
      }
      return selectedDocument.document.variables ?? [];
    }
    if (selectedDocument?.type === "firm-file") {
      const selectedFirmFileVariables = selectedDocument.document.variables;
      const generatedFirmFileVariables = generatedPreviewFirmFile?.variables;
      const vars = mergeFirmFileVariableLists(
        Array.isArray(selectedFirmFileVariables)
          ? selectedFirmFileVariables
          : undefined,
        Array.isArray(generatedFirmFileVariables)
          ? generatedFirmFileVariables
          : undefined,
      );
      if (isGlobalSignatureWizard && globalSignatureFlowKind === "firm") {
        return vars;
      }
      if (!selectedMatterId) {
        return [];
      }
      return vars;
    }
    return [];
  }, [
    currentGenerationKey,
    generatedPreviewFirmFile?.variables,
    generatedPreviewKey,
    generatedPreviewMatterFile,
    globalSignatureFlowKind,
    isGlobalSignatureWizard,
    selectedDocument,
    selectedMatterId,
  ]);
  const mappableVariables = selectedDocumentVariables.filter(
    (variable) =>
      !variable.startsWith("Signer") && !variable.startsWith("UserSigner"),
  );
  const selectedTemplateFileName =
    selectedMatterTemplate &&
    (getFileNameParts(selectedMatterTemplate.name).fileExtension
      ? selectedMatterTemplate.name
      : selectedMatterTemplate.presignedUrlKey);

  useEffect(() => {
    if (!open) {
      setCurrentStep(0);
      setSelectedMatterId(matterId ?? "");
      setSelectedMatterFileId("");
      setSelectedMatterTemplateId("");
      setSelectedFirmFile(null);
      setSelectedDestinationFolderId("");
      setMappedVariableValues({});
      setDeliveryMethod("email");
      setSigners([]);
      setRecipientSlotOrder([]);
      setIsOrdered(false);
      setExpirationDate("");
      setExpirationDateError(null);
      setGeneratedPreviewMatterFile(null);
      setGeneratedPreviewFirmFile(null);
      setGeneratedPreviewKey("");
      setLayoutFields([]);
      setIsSubmittingSignatureRequest(false);
      setSubmissionAction(null);
      setIsMissingVariablesModalOpen(false);
      setMissingVariableCount(0);
      setNotificationConfirmationDescription(null);
      setGlobalSignatureFlowKind("matter");
    }
  }, [matterId, open]);

  useEffect(() => {
    if (!open) {
      return;
    }

    if (matterId) {
      setSelectedMatterId(matterId);
    }
  }, [matterId, open]);

  const effectiveSignatureSteps = useMemo(
    () => getEffectiveSignatureRequestSteps(selectedDocument),
    [selectedDocument],
  );
  const previousEffectiveSignatureStepsRef = useRef(effectiveSignatureSteps);

  useEffect(() => {
    if (!open) {
      return;
    }
    setCurrentStep((prev) => {
      const previousSteps = previousEffectiveSignatureStepsRef.current;
      const previousStepId = previousSteps[prev]?.id;
      const nextStepIndex = effectiveSignatureSteps.findIndex(
        (step) => step.id === previousStepId,
      );

      if (nextStepIndex !== -1) {
        return nextStepIndex;
      }

      return Math.min(prev, Math.max(0, effectiveSignatureSteps.length - 1));
    });
  }, [effectiveSignatureSteps, open]);

  useEffect(() => {
    previousEffectiveSignatureStepsRef.current = effectiveSignatureSteps;
  }, [effectiveSignatureSteps]);

  const isLastStep = currentStep === effectiveSignatureSteps.length - 1;
  const isSlideoverCloseDirty = selectedDocument !== null;
  const isGeneratingPreview =
    generateMatterTemplateDocument.isPending ||
    generateFirmFileDocument.isPending ||
    generateMatterFilePdf.isPending ||
    generateFirmFilePdf.isPending ||
    isMaskingGeneratedPdf;
  const hasAnySigner = signers.length > 0;
  const hasValidExpirationDate =
    Boolean(expirationDate) && !expirationDateError;
  const isContinueDisabled =
    (effectiveSignatureSteps[currentStep]?.id === "select-document" &&
      !selectedDocument) ||
    (effectiveSignatureSteps[currentStep]?.id === "send-request" &&
      !hasAnySigner) ||
    (effectiveSignatureSteps[currentStep]?.id === "map-variables" &&
      isGeneratingPreview) ||
    (effectiveSignatureSteps[currentStep]?.id === "destination" &&
      selectedDocument?.type !== "matter-template" &&
      !selectedDocumentIsPdf &&
      isGeneratingPreview);

  const activeRequestMatterFile =
    generatedPreviewKey === currentGenerationKey
      ? (generatedPreviewMatterFile ?? selectedMatterFile)
      : selectedMatterFile;

  const activeRequestFirmFile =
    selectedDocument?.type === "firm-file"
      ? generatedPreviewKey === currentGenerationKey
        ? (generatedPreviewFirmFile ?? selectedFirmFile)
        : selectedFirmFile
      : null;

  const canEditGeneratedPreview =
    generatedPreviewKey === currentGenerationKey &&
    (selectedDocument?.type === "firm-file"
      ? Boolean(
          generatedPreviewFirmFile &&
            generatedPreviewFirmFile.id !== selectedFirmFile?.id,
        ) &&
        hasActionPermission(
          ACTION_PERMISSION_KEYS.firmFile,
          ACTION_PERMISSION_VALUES.update,
        )
      : Boolean(
          generatedPreviewMatterFile &&
            generatedPreviewMatterFile.id !== selectedMatterFile?.id,
        ) &&
        hasActionPermission(
          ACTION_PERMISSION_KEYS.matterFile,
          ACTION_PERMISSION_VALUES.update,
        ));

  const retryEditedDocumentRefresh = () => {
    if (selectedDocument?.type === "firm-file" && generatedPreviewFirmFile) {
      void handleFirmFileSaved(generatedPreviewFirmFile.id);
    } else if (generatedPreviewMatterFile) {
      void handleMatterFileSaved(generatedPreviewMatterFile.id);
    }
  };

  const resetSourceDependentState = () => {
    setMappedVariableValues({});
    setGeneratedPreviewMatterFile(null);
    setGeneratedPreviewFirmFile(null);
    setGeneratedPreviewKey("");
    setSigners([]);
    setRecipientSlotOrder([]);
    setExpirationDate("");
    setExpirationDateError(null);
    setLayoutFields([]);
  };

  const resetAllSelectionState = () => {
    setSelectedMatterFileId("");
    setSelectedMatterTemplateId("");
    setSelectedFirmFile(null);
    setSelectedDestinationFolderId("");
    setGlobalSignatureFlowKind("matter");
    setIsOrdered(false);
    resetSourceDependentState();
  };

  const handleSelectedMatterChange = (nextMatterId: string) => {
    if (!nextMatterId || nextMatterId === selectedMatterId) {
      return;
    }

    setSelectedMatterId(nextMatterId);
    resetAllSelectionState();
  };

  const handleSelectedMatterFileChange = (file: MatterFile) => {
    setSelectedMatterId(file.matterId);
    setSelectedMatterFileId(file.id);
    setSelectedMatterTemplateId("");
    setSelectedFirmFile(null);
    setSelectedDestinationFolderId("");
    resetSourceDependentState();
  };

  const handleSelectedMatterTemplateIdChange = (id: MatterTemplate["id"]) => {
    setSelectedMatterTemplateId(id);
    setSelectedMatterFileId("");
    setSelectedFirmFile(null);
    setSelectedDestinationFolderId("");
    resetSourceDependentState();
  };

  const handleDirectFirmFileSelected = (file: FirmFile) => {
    setSelectedMatterId("");
    setSelectedFirmFile(file);
    setSelectedMatterFileId("");
    setSelectedMatterTemplateId("");
    setSelectedDestinationFolderId("");
    resetSourceDependentState();
  };

  const filledContactSigners = useMemo(
    () => signers.filter(isContactSigner).map((signer) => signer.contact),
    [signers],
  );

  useEffect(() => {
    const signerIds = new Set(signers.map(getSignatureRequestSignerId));

    setLayoutFields((currentFields) => {
      const validFields = currentFields.filter((field) =>
        signerIds.has(field.signerId),
      );

      return validFields.length === currentFields.length
        ? currentFields
        : validFields;
    });
  }, [signers]);

  const matterSignatureRequestFields = useMemo<
    MatterSignatureRequestV2FieldPayload[] | null
  >(() => {
    return buildMatterSignatureRequestV2FieldsPayload(layoutFields, signers);
  }, [layoutFields, signers]);
  const firmSignatureRequestFields = useMemo<
    FirmSignatureRequestFieldPayload[] | null
  >(() => {
    return buildFirmSignatureRequestFieldsPayload(layoutFields, signers);
  }, [layoutFields, signers]);

  const isFinalStepReady = hasAnySigner && hasValidExpirationDate;

  const notificationWarningDescription = useMemo(
    () =>
      buildSignatureRequestNotificationWarningDescription(
        deliveryMethod,
        filledContactSigners,
      ),
    [deliveryMethod, filledContactSigners],
  );

  const handleSubmitSignatureRequest = useCallback(
    async (options?: { sendNotifications?: boolean }) => {
      if (isRefreshingEditedDocument || documentRefreshError) {
        toast.error("Reload the edited document before sending.");
        return;
      }
      if (!canCreateSelectedSignatureRequest) {
        toast.error(
          "You do not have permission to create this signature request.",
        );
        return;
      }
      const shouldSendNotifications = options?.sendNotifications !== false;

      const parsedExpiration = dayjs(expirationDate).startOf("day");
      const today = dayjs().startOf("day");
      if (!parsedExpiration.isValid()) {
        setExpirationDateError("Please select a valid expiration date.");
        return;
      }
      if (parsedExpiration.isBefore(today)) {
        setExpirationDateError("Expiration Date cannot be in the past.");
        return;
      }
      setExpirationDateError(null);

      if (selectedDocument?.type === "firm-file") {
        const firmFile = activeRequestFirmFile;

        if (!firmFile?.id) {
          toast.error("Please select a document before sending.");
          return;
        }
        if (!firmSignatureRequestFields) {
          setIsSubmittingSignatureRequest(false);
          setSubmissionAction(null);
          toast.error(
            "Unable to save signature fields for the selected signer.",
          );
          return;
        }

        setSubmissionAction(shouldSendNotifications ? "send" : "create-only");
        setIsSubmittingSignatureRequest(true);

        try {
          const contactChannels: Array<"email" | "sms"> = [];
          let firmPhoneId: string | undefined;

          if (shouldSendNotifications) {
            if (deliveryMethod === "email" || deliveryMethod === "both") {
              contactChannels.push("email");
            }

            if (
              (deliveryMethod === "text" || deliveryMethod === "both") &&
              signers.some(isContactSigner)
            ) {
              const firmPhones = await queryClient.fetchQuery(
                getFirmPhonesQueryOptions(),
              );
              const selectedFirmPhone = firmPhones.data.at(0);

              if (selectedFirmPhone) {
                contactChannels.push("sms");
                firmPhoneId = selectedFirmPhone.id;
              } else {
                toast.error(
                  "No firm phone is available, so text notifications could not be sent automatically.",
                );
              }
            }
          }

          await createFirmSignatureRequestV3({
            fileId: firmFile.id,
            fileIdDestination: selectedDestinationFolderId || null,
            expirationDate,
            fields: firmSignatureRequestFields,
            contactConnections: signers
              .map((signer, idx) => ({ signer, index: idx, order: idx + 1 }))
              .filter(({ signer }) => isContactSigner(signer))
              .map(({ signer, index, order }) => ({
                contactId: (
                  signer as Extract<SignatureRequestSigner, { kind: "contact" }>
                ).contact.id,
                label: `Signer${getSignatureRequestSignerSlotIndex(signers, index) + 1}`,
                ...(isOrdered ? { signerOrder: order } : {}),
              })),
            userConnections: signers
              .map((signer, idx) => ({ signer, index: idx, order: idx + 1 }))
              .filter(({ signer }) => isUserSigner(signer))
              .map(({ signer, index, order }) => ({
                userId: (
                  signer as Extract<SignatureRequestSigner, { kind: "user" }>
                ).user.id,
                label: `UserSigner${getSignatureRequestSignerSlotIndex(signers, index) + 1}`,
                ...(isOrdered ? { signerOrder: order } : {}),
              })),
            notifications: {
              contactChannels,
              ...(firmPhoneId ? { firmPhoneId } : {}),
              notifyUsersByEmail: shouldSendNotifications,
            },
          });

          await invalidateSignatureRequestCreateRelatedQueries(queryClient);

          dispatchSignatureRequestCreated();
          setOpen(false);

          toast.success(
            shouldSendNotifications
              ? "Signature request created and notifications sent."
              : "Signature request created.",
          );
        } catch (error) {
          console.error(error);
          toastAPIErrorMessage(
            error as AxiosError<APIError>,
            "Error submitting signature request",
          );
        } finally {
          setIsSubmittingSignatureRequest(false);
          setSubmissionAction(null);
        }

        return;
      }

      if (!matter) {
        toast.error("Unable to submit signature request.");
        return;
      }

      const requestMatterFile = activeRequestMatterFile;

      if (!requestMatterFile?.id) {
        toast.error("Please select a document before sending.");
        return;
      }

      setSubmissionAction(shouldSendNotifications ? "send" : "create-only");
      setIsSubmittingSignatureRequest(true);

      if (!matterSignatureRequestFields) {
        setIsSubmittingSignatureRequest(false);
        setSubmissionAction(null);
        toast.error("Unable to save signature fields for the selected signer.");
        return;
      }

      try {
        const contactChannels: Array<"email" | "sms"> = [];
        let firmPhoneId: string | undefined;

        if (shouldSendNotifications) {
          if (deliveryMethod === "email" || deliveryMethod === "both") {
            contactChannels.push("email");
          }

          if (
            (deliveryMethod === "text" || deliveryMethod === "both") &&
            signers.some(isContactSigner)
          ) {
            const firmPhones = await queryClient.fetchQuery(
              getFirmPhonesQueryOptions(),
            );
            const selectedFirmPhone = firmPhones.data.at(0);

            if (selectedFirmPhone) {
              contactChannels.push("sms");
              firmPhoneId = selectedFirmPhone.id;
            } else {
              toast.error(
                "No firm phone is available, so text notifications could not be sent automatically.",
              );
            }
          }
        }

        await createMatterSignatureRequestV3({
          matterId: matter.id,
          fileId: requestMatterFile.id,
          fileIdDestination: selectedDestinationFolderId || "",
          expirationDate,
          contactConnections: signers
            .map((signer, idx) => ({ signer, index: idx, order: idx + 1 }))
            .filter(({ signer }) => isContactSigner(signer))
            .map(({ signer, index, order }) => ({
              contactId: (
                signer as Extract<SignatureRequestSigner, { kind: "contact" }>
              ).contact.id,
              label: `Signer${getSignatureRequestSignerSlotIndex(signers, index) + 1}`,
              ...(isOrdered ? { signerOrder: order } : {}),
            })),
          userConnections: signers
            .map((signer, idx) => ({ signer, index: idx, order: idx + 1 }))
            .filter(({ signer }) => isUserSigner(signer))
            .map(({ signer, index, order }) => ({
              userId: (
                signer as Extract<SignatureRequestSigner, { kind: "user" }>
              ).user.id,
              label: `UserSigner${getSignatureRequestSignerSlotIndex(signers, index) + 1}`,
              ...(isOrdered ? { signerOrder: order } : {}),
            })),
          fields: matterSignatureRequestFields,
          notifications: {
            contactChannels,
            ...(firmPhoneId ? { firmPhoneId } : {}),
            notifyUsersByEmail: shouldSendNotifications,
          },
        });

        await invalidateSignatureRequestCreateRelatedQueries(queryClient);

        dispatchSignatureRequestCreated();
        setOpen(false);

        toast.success(
          shouldSendNotifications
            ? "Signature request created and notifications sent."
            : "Signature request created.",
        );
      } catch (error) {
        console.error(error);
        toastAPIErrorMessage(
          error as AxiosError<APIError>,
          "Error submitting signature request",
        );
      } finally {
        setIsSubmittingSignatureRequest(false);
        setSubmissionAction(null);
      }
    },
    [
      activeRequestMatterFile,
      documentRefreshError,
      deliveryMethod,
      expirationDate,
      firmSignatureRequestFields,
      isOrdered,
      matter,
      matterSignatureRequestFields,
      queryClient,
      selectedDestinationFolderId,
      activeRequestFirmFile,
      isRefreshingEditedDocument,
      canCreateSelectedSignatureRequest,
      selectedDocument,
      setOpen,
      signers,
    ],
  );

  const activeStepContent = (() => {
    switch (effectiveSignatureSteps[currentStep]?.id) {
      case "destination":
        return (
          <DestinationStep
            matterId={selectedMatterId}
            useFirmLibraryDestination={useFirmLibraryDestination}
            selectedDocument={selectedDocument}
            selectedDestinationFolderId={selectedDestinationFolderId}
            setSelectedDestinationFolderId={setSelectedDestinationFolderId}
          />
        );
      case "map-variables":
        return (
          <MapVariablesStep
            subItem={subItem}
            matterId={selectedMatterId}
            isFirmSignatureFlow={
              isGlobalSignatureWizard && globalSignatureFlowKind === "firm"
            }
            selectedDocument={selectedDocument}
            selectedDocumentVariables={selectedDocumentVariables}
            mappedVariableValues={mappedVariableValues}
            setMappedVariableValues={setMappedVariableValues}
            signers={signers}
            setSigners={setSigners}
          />
        );
      case "send-request":
        return (
          <SendRequestStep
            documentVariables={selectedDocumentVariables}
            recipientSlotOrder={recipientSlotOrder}
            setRecipientSlotOrder={setRecipientSlotOrder}
            matterId={selectedMatterId}
            previewDocument={
              selectedDocument?.type === "firm-file"
                ? generatedPreviewKey === currentGenerationKey &&
                  generatedPreviewFirmFile
                  ? {
                      type: "firm-file",
                      document: generatedPreviewFirmFile,
                    }
                  : selectedDocument
                : generatedPreviewKey === currentGenerationKey &&
                    generatedPreviewMatterFile
                  ? {
                      type: "matter-file",
                      document: generatedPreviewMatterFile,
                    }
                  : selectedDocument
            }
            signers={signers}
            setSigners={setSigners}
            isOrdered={isOrdered}
            setIsOrdered={setIsOrdered}
            layoutFields={layoutFields}
            onLayoutFieldsChange={setLayoutFields}
          />
        );
      case "review-send":
        return (
          <ReviewAndSendStep
            matterId={selectedMatterId}
            previewDocument={
              selectedDocument?.type === "firm-file"
                ? generatedPreviewKey === currentGenerationKey &&
                  generatedPreviewFirmFile
                  ? { type: "firm-file", document: generatedPreviewFirmFile }
                  : selectedDocument
                : generatedPreviewKey === currentGenerationKey &&
                    generatedPreviewMatterFile
                  ? {
                      type: "matter-file",
                      document: generatedPreviewMatterFile,
                    }
                  : selectedDocument
            }
            expirationDate={expirationDate}
            setExpirationDate={setExpirationDate}
            expirationDateError={expirationDateError}
            clearExpirationDateError={() => {
              setExpirationDateError(null);
            }}
            deliveryMethod={deliveryMethod}
            setDeliveryMethod={setDeliveryMethod}
            layoutFields={layoutFields}
            signers={signers}
            setSigners={setSigners}
            canEditDocumentFiles={canEditGeneratedPreview}
            onMatterFileSaved={handleMatterFileSaved}
            onFirmFileSaved={handleFirmFileSaved}
            isRefreshingEditedDocument={isRefreshingEditedDocument}
            documentRefreshError={documentRefreshError}
            onRetryDocumentRefresh={retryEditedDocumentRefresh}
          />
        );
      case "select-document":
      default:
        return (
          <SelectDocumentStep
            slideoverOpen={open}
            matterId={selectedMatterId || undefined}
            isGlobalMode={!matterId}
            selectedMatterFileId={selectedMatterFileId}
            selectedMatterTemplateId={selectedMatterTemplateId}
            selectedFirmFile={selectedFirmFile}
            setSelectedMatterId={handleSelectedMatterChange}
            onMatterFileSelected={handleSelectedMatterFileChange}
            onMatterTemplateSelected={handleSelectedMatterTemplateIdChange}
            onDirectFirmFileSelected={handleDirectFirmFileSelected}
            onGlobalSignatureFlowKindSet={setGlobalSignatureFlowKind}
          />
        );
    }
  })();

  const missingMappableVariableCount = useMemo(() => {
    return Array.from(
      new Set(mappableVariables.map(getBaseKeyForSignatureRequestVariable)),
    ).filter((storageKey) => !mappedVariableValues[storageKey]).length;
  }, [mappableVariables, mappedVariableValues]);

  const missingVariablesDescription =
    missingVariableCount === 1
      ? "1 variable is still empty. You can continue, but the generated document may be incomplete."
      : `${missingVariableCount} variables are still empty. You can continue, but the generated document may be incomplete.`;

  const generatedDocumentInputs = useMemo(
    () =>
      buildSignatureRequestMergeInputs(mappableVariables, mappedVariableValues),
    [mappableVariables, mappedVariableValues],
  );

  const maskGeneratedPdf = useCallback(
    async <T extends MatterFile | FirmFile>(
      file: T,
      fileType: T extends MatterFile ? "matter-file" : "firm-file",
    ) => {
      setIsMaskingGeneratedPdf(true);
      try {
        return await maskGeneratedSignatureRequestPdf(file, fileType);
      } finally {
        setIsMaskingGeneratedPdf(false);
      }
    },
    [],
  );

  const handleContinue = async (options?: { bypassMissingCheck?: boolean }) => {
    const currentStepId = effectiveSignatureSteps[currentStep]?.id;
    const shouldConvertNonTemplateDocument =
      currentStepId === "destination" &&
      Boolean(selectedDocument) &&
      selectedDocument?.type !== "matter-template" &&
      !selectedDocumentIsPdf;

    if (shouldConvertNonTemplateDocument) {
      if (
        generatedPreviewKey === currentGenerationKey &&
        ((selectedDocument?.type === "firm-file" && generatedPreviewFirmFile) ||
          (selectedDocument?.type === "matter-file" &&
            generatedPreviewMatterFile))
      ) {
        setCurrentStep((prev) =>
          Math.min(prev + 1, effectiveSignatureSteps.length - 1),
        );
        return;
      }

      if (selectedDocument?.type === "firm-file") {
        const firmFileName = buildUniqueGeneratedFirmFileName({
          existingFileNames: (firmFilesData?.data ?? []).map(
            (file) => file.name,
          ),
          outputExtension: ".pdf",
          sourceFileName: selectedDocument.document.name,
        });
        const generatedFile = await generateFirmFilePdf.mutateAsync({
          id: selectedDocument.document.id,
          name: firmFileName,
        });

        const generatedFirmFile = await maskGeneratedPdf(
          generatedFile.data,
          "firm-file",
        );

        setGeneratedPreviewFirmFile(generatedFirmFile);
        setGeneratedPreviewMatterFile(null);
        setGeneratedPreviewKey(currentGenerationKey);
      } else if (selectedDocument?.type === "matter-file" && matter) {
        const matterFileName = buildUniqueGeneratedFileName({
          existingFileNames: matter.files.map((file) => file.name),
          outputExtension: ".pdf",
          sourceFileName: selectedDocument.document.name,
        });
        const generatedFile = await generateMatterFilePdf.mutateAsync({
          matterId: matter.id,
          matterFileId: selectedDocument.document.id,
          matterFileName,
        });

        const generatedMatterFile = await maskGeneratedPdf(
          generatedFile.data,
          "matter-file",
        );

        setGeneratedPreviewMatterFile(generatedMatterFile);
        setGeneratedPreviewFirmFile(null);
        setGeneratedPreviewKey(currentGenerationKey);
      }
    }

    if (currentStepId === "map-variables") {
      if (
        !options?.bypassMissingCheck &&
        mappableVariables.length > 0 &&
        missingMappableVariableCount > 0
      ) {
        setMissingVariableCount(missingMappableVariableCount);
        setIsMissingVariablesModalOpen(true);
        return;
      }

      if (generatedPreviewKey === currentGenerationKey) {
        if (
          selectedDocument?.type === "firm-file" &&
          generatedPreviewFirmFile
        ) {
          setCurrentStep((prev) =>
            Math.min(prev + 1, effectiveSignatureSteps.length - 1),
          );
          return;
        }

        if (
          selectedDocument?.type !== "firm-file" &&
          generatedPreviewMatterFile
        ) {
          setCurrentStep((prev) =>
            Math.min(prev + 1, effectiveSignatureSteps.length - 1),
          );
          return;
        }
      }

      if (selectedDocument?.type === "firm-file") {
        if (!selectedFirmFile) {
          return;
        }

        const firmFileName = buildUniqueGeneratedFirmFileName({
          existingFileNames: (firmFilesData?.data ?? []).map(
            (file) => file.name,
          ),
          outputExtension: ".pdf",
          sourceFileName: selectedFirmFile.name,
        });
        const generatedFile =
          selectedDocumentIsPdf || mappableVariables.length === 0
            ? await generateFirmFilePdf.mutateAsync({
                id: selectedFirmFile.id,
                name: firmFileName,
              })
            : await generateFirmFileDocument.mutateAsync({
                extension: ".pdf",
                idLocation: selectedDestinationFolderId || "",
                name: firmFileName,
                id: selectedFirmFile.id,
                inputs: generatedDocumentInputs,
              });

        const generatedFirmFile = await maskGeneratedPdf(
          generatedFile.data,
          "firm-file",
        );
        setGeneratedPreviewFirmFile(generatedFirmFile);
        setSelectedFirmFile((prev) => {
          if (!prev || prev.id !== generatedFirmFile.id) {
            return prev;
          }
          const fromGenerate = generatedFirmFile.variables;
          if (!fromGenerate?.length) {
            return prev;
          }
          return { ...prev, variables: fromGenerate };
        });
        setGeneratedPreviewMatterFile(null);
        setGeneratedPreviewKey(currentGenerationKey);
      } else {
        if (!selectedDocument || !matter) {
          return;
        }

        const matterFileName = buildUniqueGeneratedFileName({
          existingFileNames: matter.files.map((file) => file.name),
          outputExtension: ".pdf",
          sourceFileName:
            selectedMatterFile?.name ??
            selectedTemplateFileName ??
            "signature-request-document.docx",
        });
        const generatedFile =
          selectedDocument.type === "matter-file" &&
          mappableVariables.length === 0
            ? await generateMatterFilePdf.mutateAsync({
                matterId: matter.id,
                matterFileId: selectedDocument.document.id,
                matterFileName,
              })
            : selectedDocument.type === "matter-template"
              ? await generateMatterTemplateDocument.mutateAsync({
                  id: selectedDocument.document.id,
                  matterTemplateInputs: generatedDocumentInputs,
                  matterId: matter.id,
                  matterFileName,
                  matterFileIdLocation: selectedDestinationFolderId || "",
                  matterFileExtension: ".pdf",
                })
              : await generateMatterTemplateDocument.mutateAsync({
                  matterFileId: selectedDocument.document.id,
                  matterFileInputs: generatedDocumentInputs,
                  matterId: matter.id,
                  matterFileName,
                  matterFileIdLocation: selectedDestinationFolderId || "",
                  matterFileExtension: ".pdf",
                });

        const generatedMatterFile = await maskGeneratedPdf(
          generatedFile.data,
          "matter-file",
        );

        setGeneratedPreviewMatterFile(generatedMatterFile);
        setGeneratedPreviewFirmFile(null);
        setGeneratedPreviewKey(currentGenerationKey);
      }
    }

    setCurrentStep((prev) =>
      Math.min(prev + 1, effectiveSignatureSteps.length - 1),
    );
  };

  const handleCreateAndSend = () => {
    if (notificationWarningDescription) {
      setNotificationConfirmationDescription(notificationWarningDescription);
      return;
    }

    void handleSubmitSignatureRequest({ sendNotifications: true });
  };

  const handleCreateWithoutSending = () => {
    void handleSubmitSignatureRequest({ sendNotifications: false });
  };

  return (
    <AppModal
      open={open}
      onOpenChange={setOpen}
      title={title}
      description=""
      isDirty={isSlideoverCloseDirty}
      loading={isGeneratingPreview}
      bodyContained
      overlayClassName="z-[98] bg-black/45 backdrop-blur-md"
      className="top-0 left-0 z-[99] h-[100dvh] !max-h-[100dvh] w-screen !max-w-none translate-x-0 translate-y-0 rounded-none sm:w-screen"
      footer={
        <div className="flex w-full flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <StepProgress
            steps={effectiveSignatureSteps}
            currentStep={currentStep}
            className="w-full min-w-0 sm:max-w-3xl"
          />
          <div className="flex shrink-0 justify-end gap-x-2">
            <FormCancelButton
              setOpen={setOpen}
              isDirty={isSlideoverCloseDirty}
            />
            <Button
              type="button"
              variant="outline"
              disabled={currentStep === 0 || isRefreshingEditedDocument}
              onClick={() => {
                setCurrentStep((prev) => Math.max(prev - 1, 0));
              }}
            >
              Back
            </Button>
            {isLastStep ? (
              <div className="flex">
                <Button
                  type="button"
                  variant="light-primary"
                  className="rounded-r-none"
                  disabled={
                    !isFinalStepReady ||
                    isSubmittingSignatureRequest ||
                    isRefreshingEditedDocument ||
                    documentRefreshError ||
                    !canCreateSelectedSignatureRequest
                  }
                  onClick={handleCreateAndSend}
                >
                  {isSubmittingSignatureRequest
                    ? submissionAction === "send"
                      ? "Creating and sending..."
                      : "Creating..."
                    : "Create and send"}
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      type="button"
                      variant="light-primary"
                      className="border-primary/20 rounded-l-none border-l px-2"
                      aria-label="More create signature request actions"
                      disabled={
                        !isFinalStepReady ||
                        isSubmittingSignatureRequest ||
                        isRefreshingEditedDocument ||
                        documentRefreshError ||
                        !canCreateSelectedSignatureRequest
                      }
                    >
                      <ChevronDown className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={handleCreateWithoutSending}>
                      Create without sending
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            ) : (
              <Button
                type="button"
                disabled={isContinueDisabled}
                onClick={() => {
                  void handleContinue().catch((error: unknown) => {
                    toastAPIErrorMessage(
                      error as AxiosError<APIError>,
                      "Unable to convert this document to PDF.",
                    );
                  });
                }}
              >
                {effectiveSignatureSteps[currentStep]?.id === "map-variables" &&
                isGeneratingPreview
                  ? "Generating PDF..."
                  : effectiveSignatureSteps[currentStep]?.id ===
                        "destination" &&
                      selectedDocument?.type !== "matter-template" &&
                      !selectedDocumentIsPdf &&
                      isGeneratingPreview
                    ? "Converting PDF..."
                    : "Continue"}
              </Button>
            )}
          </div>
        </div>
      }
    >
      <div
        className="flex min-h-0 grow flex-col overflow-hidden"
        data-matter-id={selectedMatterId || matterId}
      >
        <div className="flex min-h-0 flex-1 overflow-hidden">
          <div className="flex min-h-0 w-full flex-1">{activeStepContent}</div>
        </div>
      </div>
      <ActionConfirmationModal
        open={isMissingVariablesModalOpen}
        setOpen={setIsMissingVariablesModalOpen}
        title="Some variables are empty"
        description={missingVariablesDescription}
        buttonText="Continue"
        cancelText="Go back"
        actionVariant="default"
        afterActionFn={() => {
          setIsMissingVariablesModalOpen(false);
          void handleContinue({ bypassMissingCheck: true });
        }}
        onCancel={() => {
          setIsMissingVariablesModalOpen(false);
          setMissingVariableCount(0);
        }}
      />
      <ActionConfirmationModal
        open={Boolean(notificationConfirmationDescription)}
        setOpen={(isOpen) => {
          if (!isOpen) {
            setNotificationConfirmationDescription(null);
          }
        }}
        title="Continue sending?"
        description={notificationConfirmationDescription ?? ""}
        buttonText="Create and send"
        cancelText="Cancel"
        actionVariant="default"
        afterActionFn={() => {
          setNotificationConfirmationDescription(null);
          void handleSubmitSignatureRequest({ sendNotifications: true });
        }}
      />
    </AppModal>
  );
};
